import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { type EntityDrawer, EntityExplorerContext, type EntityType } from './EntityExplorerContext';

interface EntityExplorerProviderProps {
  children: React.ReactNode;
}

/**
 * Maximum number of drawers that can be open simultaneously
 * Limited to prevent z-index conflicts and UX issues
 */
const MAX_DRAWERS = 4;

/**
 * Base z-index for the first drawer
 * Subsequent drawers increment from this value
 */
const BASE_Z_INDEX = 85;

/**
 * Provider component for EntityExplorer
 * Manages state for stacked drawers and provides context to children
 */
export const EntityExplorerProvider: React.FC<EntityExplorerProviderProps> = ({ children }) => {
  const [drawers, setDrawers] = useState<EntityDrawer[]>([]);

  /**
   * Opens a new drawer for the specified entity
   * Adds it to the top of the stack
   */
  const openDrawer = useCallback(
    (type: EntityType, entityId: string, title: string) => {
      // Prevent opening more than MAX_DRAWERS
      if (drawers.length >= MAX_DRAWERS) {
        // Maximum drawers reached - silently ignore
        return;
      }

      // Generate unique ID for this drawer
      const id = `drawer-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      // Calculate z-index based on current stack depth
      const zIndex = BASE_Z_INDEX + drawers.length;

      const newDrawer: EntityDrawer = {
        id,
        type,
        entityId,
        title,
        zIndex,
      };

      setDrawers((prev) => [...prev, newDrawer]);
    },
    [drawers.length],
  );

  /**
   * Closes a specific drawer by its ID
   */
  const closeDrawer = useCallback((id: string) => {
    setDrawers((prev) => prev.filter((drawer) => drawer.id !== id));
  }, []);

  /**
   * Closes the topmost (most recently opened) drawer
   */
  const closeTopDrawer = useCallback(() => {
    setDrawers((prev) => {
      if (prev.length === 0) return prev;
      return prev.slice(0, -1);
    });
  }, []);

  /**
   * Closes all open drawers
   */
  const closeAll = useCallback(() => {
    setDrawers([]);
  }, []);

  const contextValue = useMemo(
    () => ({
      drawers,
      openDrawer,
      closeDrawer,
      closeTopDrawer,
      closeAll,
    }),
    [drawers, openDrawer, closeDrawer, closeTopDrawer, closeAll],
  );

  return (
    <EntityExplorerContext.Provider value={contextValue}>{children}</EntityExplorerContext.Provider>
  );
};
